// MorningCoffee.jsx — the coffee section of Morning Mode, rendered as the
// prototype's ONE-CARD wizard (docs/pronto-prototype-spec.md · "## Morning").
//
// The single glass card holds, top→bottom:
//   • Bag header  — swatch + branch badge + beans
//   • "Today's plan" eyebrow + branch segmented (Repeat/Tweak/New Bag/Blend)
//   • Auto-picked "why" line
//   • branch body — Repeat's readout inline; Tweak/New Bag/Blend embedded so the
//     whole thing stays a single card with the segmented always on top.
//
// Behaviour is unchanged from before: branch resolution (resolveMorningBranch),
// the rating gate, the started-brew transaction (log → decrement → +40m rating
// push → complete), and each branch's own data wiring. This file adds only
// presentation + the Aiden-profile banner (per spec). The Repeat readout gains
// the ✎ Adjust stepper (dose/grind per-brew, temp → Aiden profile) and the
// Aiden-profile banner.

import { useEffect, useMemo, useState } from "react";
import { Segmented } from "./pronto";
import { BrewRating } from "./BrewRating";
import { MorningNewBag } from "./MorningNewBag";
import { MorningTweak } from "./MorningTweak";
import { MorningBlend } from "./MorningBlend";
import { AidenProfileBanner } from "./AidenProfileBanner";
import { resolveMorningBranch } from "../lib/coffee-branch";
import { brewSuggestionFromWeather } from "../lib/weather";
import { shiftAidenProfileTempF, coldBrewPlan, applyProfileOverrides } from "../lib/coffee";
import { RecipeNotes } from "./RecipeNotes";
import { canisterBean, getOpenBeans, bagWeightLeft, maxCupsForWeight } from "../lib/brew-helpers";
import { scheduleRatingPush } from "../lib/scheduled-push";
import { supabase } from "../lib/supabase";

const DEFAULT_CUPS = 8; // spec default
const BRANCHES = [
  { id: "repeat", label: "Repeat" },
  { id: "tweak", label: "Tweak" },
  { id: "new_bag", label: "New Bag" },
  { id: "blend", label: "Blend" },
];
const MODES = [{ id: "hot", label: "Hot" }, { id: "cold", label: "Cold brew" }];

const CtoF = (c) => (c == null ? null : Math.round((c * 9) / 5 + 32));
const FtoC = (f) => Math.round(((f - 32) * 5) / 9);

// Per-branch header chrome (badge eyebrow, bag swatch gradient, foot line).
const BADGE = { repeat: "Repeat · verbatim", tweak: "Tweak · adjusted", new_bag: "New bag · starting recipe", blend: "Blend · one-off" };
const FOOT = { repeat: "Zero decisions · no AI call", tweak: "Rating prompt in ~40 min", new_bag: "Depletion tracking starts now", blend: "Decrements both bags by share" };
const SWATCH = {
  repeat: "linear-gradient(150deg,#8a4a2f,#4a2416)",
  tweak: "linear-gradient(150deg,#8a4a2f,#4a2416)",
  new_bag: "linear-gradient(150deg,#c98a4a,#7a4a24)",
  blend: "linear-gradient(120deg,#8a4a2f 0%,#8a4a2f 50%,#c98a4a 50%,#c98a4a 100%)",
};

function pickActiveBean(store) {
  return canisterBean(store.beans, store.preferences)
    || getOpenBeans(store.beans, store.preferences)[0]
    || (store.beans || []).find((b) => b.status !== "finished")
    || null;
}

// The bag-header bar shown across every branch.
function BagHeader({ branch, beans }) {
  return (
    <div
      className="flex items-center gap-[11px] px-4 py-[13px] border-b-[0.5px] border-[rgba(60,60,67,0.08)]"
      style={{ background: "rgba(63,74,99,0.06)" }}
    >
      <div
        className="w-8 h-11 rounded-[8px] flex-shrink-0"
        style={{ background: SWATCH[branch], boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.07)" }}
        aria-hidden="true"
      />
      <div className="flex-1 min-w-0">
        <div className="font-sf text-[10px] font-bold uppercase tracking-[0.1em] text-pronto-navy">{BADGE[branch]}</div>
        <div className="font-round font-bold text-[17px] text-pronto-ink truncate">{beans}</div>
      </div>
    </div>
  );
}

// The dose (60px) + Ode grind (34px) headline row.
function DoseGrind({ dose, grind, cups }) {
  return (
    <div className="flex items-end justify-between mt-0.5">
      <div className="font-round font-bold text-pronto-ink tabular" style={{ fontSize: 60, letterSpacing: "-0.04em", lineHeight: 0.85 }}>
        {dose != null ? dose : "—"}<span className="text-pronto-text-3" style={{ fontSize: 23 }}>g</span>
      </div>
      <div className="text-right">
        <div className="font-sf text-[11px] uppercase text-pronto-text-3">Ode grind</div>
        <div className="font-round font-bold text-[34px] text-pronto-ink leading-none tabular">{grind ?? "—"}</div>
      </div>
    </div>
  );
}

// The 3-up Temp / Ratio / Bloom(Steep) spec strip.
function SpecStrip({ tempDisp, ratioDisp, bloomLabel, bloomVal }) {
  const cells = [
    ["Temp", tempDisp],
    ["Ratio", ratioDisp],
    [bloomLabel, bloomVal],
  ];
  return (
    <div className="flex gap-2 mt-2">
      {cells.map(([label, value], i) => (
        <div key={i} className="flex-1 text-center rounded-[13px] py-2" style={{ background: "rgba(255,255,255,0.6)" }}>
          <div className="font-sf text-[10px] uppercase text-pronto-text-3">{label}</div>
          <div className="font-round font-bold text-[15px] text-pronto-ink tabular">{value}</div>
        </div>
      ))}
    </div>
  );
}

// The ✎ Adjust stepper panel (dose/grind per-brew, temp → Aiden profile).
function StepRow({ label, value, onDown, onUp, divider }) {
  const step = "w-[26px] h-[26px] rounded-full grid place-items-center text-[16px] font-bold text-pronto-navy select-none flex-shrink-0";
  return (
    <div className={`flex items-center py-[7px] ${divider ? "border-b-[0.5px] border-[rgba(60,60,67,0.07)]" : ""}`}>
      <span className="flex-1 font-sf text-[13px] text-pronto-ink">{label}</span>
      <div className="flex items-center gap-3">
        <button onClick={onDown} className={step} style={{ background: "rgba(63,74,99,0.1)", minWidth: 26 }} aria-label={`Decrease ${label}`}>−</button>
        <span className="font-round font-bold text-[15px] text-pronto-ink text-center min-w-[46px]">{value}</span>
        <button onClick={onUp} className={step} style={{ background: "rgba(63,74,99,0.1)", minWidth: 26 }} aria-label={`Increase ${label}`}>+</button>
      </div>
    </div>
  );
}

export function MorningCoffee({ store, session, weather, onComplete, onResolvedBranch }) {
  // Read the user's cup preference — the SAME source the Today/Daily/Coffee cards
  // use (default_cups || 8) — so the morning ritual and the dashboard agree on the
  // dose. This was hardcoded to DEFAULT_CUPS, which is why the two disagreed.
  const [defaultCups] = useState(store.preferences?.default_cups || DEFAULT_CUPS);
  // The ritual's canonical batch sizes — one tap to a 4/6/8-cup pour ("want to
  // only make 4 cups this am") without touching Settings.
  const [cupsChoice, setCupsChoice] = useState(null);
  const prefCups = cupsChoice ?? defaultCups;
  // Cold brew ready-by ("I want to drink it at 6am") — drives the steep plan.
  const [readyByTime, setReadyByTime] = useState("06:00");
  const [mode, setMode] = useState("hot");
  const [modeTouched, setModeTouched] = useState(false);

  // Open in cold on genuine cold-brew weather (≥90°F feels-like), matching the
  // Today/Daily preview, until the user picks a mode. Morning Mode has no "iced"
  // option, so the 80–90°F iced band stays hot — Japanese-style iced is a
  // hot-brewed style anyway. Weather loads async, hence the effect over an initializer.
  const suggestsCold = brewSuggestionFromWeather(weather)?.suggested_mode === "cold";
  useEffect(() => {
    if (!modeTouched && suggestsCold) setMode("cold");
  }, [suggestsCold, modeTouched]);
  const [forced, setForced] = useState(null); // user override of the auto-picked branch
  const [busy, setBusy] = useState(false);

  // Per-brew adjustments (Repeat readout).
  const [doseOverride, setDoseOverride] = useState(null);
  const [grindDelta, setGrindDelta] = useState(0);
  const [tempDelta, setTempDelta] = useState(0);
  const [adjustOpen, setAdjustOpen] = useState(false);

  const bean = useMemo(() => pickActiveBean(store), [store.beans, store.preferences]);

  const baseBrew = useMemo(() => {
    if (!bean) return null;
    return store.computeBrew({ beanId: bean.id, cups: prefCups, mode });
  }, [bean, prefCups, mode, store]);

  // Grams-left planning (the ritual's "46g left → brew 6 cups well"): when the
  // bag can't cover the full pour, scale the cups down honestly instead of
  // showing a dose the bag can't supply. Branch resolution below still uses the
  // FULL-pour dose so Blend keeps precedence when a fresh bag is on hand.
  const weightLeft = bagWeightLeft(bean);
  const scaledCups = useMemo(
    () => maxCupsForWeight({ weightLeftG: weightLeft, doseG: baseBrew?.dose_g, cups: prefCups }),
    [weightLeft, baseBrew?.dose_g, prefCups],
  );
  const cups = scaledCups ?? prefCups;

  const brew = useMemo(() => {
    if (!bean || scaledCups == null) return baseBrew;
    return store.computeBrew({ beanId: bean.id, cups: scaledCups, mode });
  }, [bean, scaledCups, mode, store, baseBrew]);

  // Cold-brew ready-by plan ("drink it at 6am"): steep duration from now to the
  // chosen time (tomorrow if it already passed), with the grind/ratio
  // compensation for short overnight steeps from coldBrewPlan.
  const coldPlan = useMemo(() => {
    if (mode !== "cold" || !brew) return null;
    const now = new Date();
    const [h, m] = (readyByTime || "06:00").split(":").map(Number);
    const readyBy = new Date(now);
    readyBy.setHours(h || 0, m || 0, 0, 0);
    if (readyBy <= now) readyBy.setDate(readyBy.getDate() + 1);
    const plan = coldBrewPlan({
      readyBy, now,
      baseSetting: brew.grind?.setting,
      baseRatio: brew.aidenProfile?.ratio ?? brew.ratio,
      grinder: store.gear?.grinder,
    });
    return plan ? { readyBy, ...plan } : null;
  }, [mode, brew, readyByTime, store.gear]);

  // The brew we display and log: cold folds the plan's ratio + grind in (ratio
  // through applyProfileOverrides so dose/bloom water recompute consistently).
  const plannedBrew = useMemo(() => {
    if (!brew || !coldPlan) return brew;
    const baseRatio = brew.aidenProfile?.ratio ?? brew.ratio;
    let out = coldPlan.ratio && coldPlan.ratio !== baseRatio
      ? applyProfileOverrides(brew, { ratio: coldPlan.ratio })
      : brew;
    if (coldPlan.grindSetting != null) {
      out = { ...out, grind: { ...out.grind, setting: coldPlan.grindSetting } };
    }
    return out;
  }, [brew, coldPlan]);

  const resolved = useMemo(() => resolveMorningBranch({
    beans: store.beans, brewLogs: store.brewLogs, checkIns: store.checkIns,
    preferences: store.preferences, doseG: baseBrew?.dose_g, cups, forced,
  }), [store.beans, store.brewLogs, store.checkIns, store.preferences, baseBrew?.dose_g, cups, forced]);

  // Report the resolved branch up so the shell can show "Morning Mode · {sub}".
  useEffect(() => { onResolvedBranch?.(resolved.branch); }, [resolved.branch, onResolvedBranch]);

  // Reset per-brew adjustments when the branch or mode changes.
  useEffect(() => { setDoseOverride(null); setGrindDelta(0); setTempDelta(0); setAdjustOpen(false); }, [resolved.branch, mode]);

  if (!bean) {
    return (
      <div className="rounded-[24px] bg-[var(--pg)] border border-[var(--pg-border)] shadow-pronto-hero p-5 text-center">
        <div className="text-[13px] text-pronto-text-2">No open bag yet. Add a bag in Coffee to start brewing.</div>
      </div>
    );
  }

  const rateYesterday = async (payload) => { await store.submitCheckIn(payload); };

  const cold = mode === "cold";
  const baseGrind = plannedBrew?.grind?.setting;
  const baseTempF = CtoF(plannedBrew?.temp_c);
  const ratio = plannedBrew?.aidenProfile?.ratio ?? plannedBrew?.ratio;
  const bloomS = plannedBrew?.aidenProfile?.bloomDuration ?? plannedBrew?.bloom_s;

  // Stepper granularity follows the user's own grinder dial (Ode: 0.1). A
  // whole-detent grinder steps in 1s; the Ode must never step a whole click.
  const grindStep = store.gear?.grinder?.step ?? 0.1;
  const grindDecimals = grindStep < 1 ? 1 : 0;

  const doseG = doseOverride ?? (plannedBrew?.dose_g != null ? Math.round(plannedBrew.dose_g) : null);
  const grindVal = baseGrind != null ? +(baseGrind + grindDelta).toFixed(grindDecimals) : null;
  const tempF = baseTempF != null ? baseTempF + tempDelta : null;
  const tempDisp = cold ? "Cold" : tempF != null ? `${tempF}°` : "—";
  const ratioDisp = ratio ? `1:${ratio}` : "—"; // cold shows the REAL planned ratio, not a hardcoded 1:8
  const bloomLabel = cold ? "Steep" : "Bloom";
  const bloomVal = cold ? (coldPlan ? `${coldPlan.durationHours}h` : "12h") : bloomS ? `${bloomS}s` : "—";

  // Build the (possibly adjusted) brew we log + push to the Aiden. A manual temp
  // nudge must move the Aiden profile's pulse temps too (shiftAidenProfileTempF),
  // not just the headline temp_c — otherwise the logged temp and the temp pushed
  // to the machine disagree.
  const adjustedBrew = plannedBrew ? {
    ...plannedBrew,
    dose_g: doseG ?? plannedBrew.dose_g,
    water_g: doseG != null && ratio ? Math.round(doseG * ratio) : plannedBrew.water_g,
    grind: { ...plannedBrew.grind, setting: grindVal ?? plannedBrew.grind?.setting },
    temp_c: tempDelta ? FtoC(baseTempF + tempDelta) : plannedBrew.temp_c,
    aidenProfile: shiftAidenProfileTempF(plannedBrew.aidenProfile, tempDelta),
  } : null;

  const startBrew = async () => {
    if (busy || !adjustedBrew) return;
    setBusy(true);
    try {
      const log = await store.logBrew(
        {
          ...adjustedBrew, bean_id: bean.id, source: "repeat",
          // Ready-by steep plan rides along into brew_data for the history view.
          ...(coldPlan ? { cold_plan: { ready_by: coldPlan.readyBy.toISOString(), duration_h: coldPlan.durationHours } } : {}),
        },
        "", null,
      );
      try {
        await scheduleRatingPush({
          supabase, userId: store.userId, brewLogId: log.id, beanName: bean.name,
          fireAt: new Date(Date.now() + 40 * 60 * 1000),
        });
      } catch (e) { console.warn("rating push schedule failed", e?.message); }
      await store.completeMorning(log.id);
      onComplete?.(log);
    } finally { setBusy(false); }
  };

  const beansLabel = [bean.roaster, bean.name].filter(Boolean).join(" · ") || bean.name;
  const isGate = resolved.needsRating && !forced;

  return (
    <div className="rounded-[24px] bg-[var(--pg)] border border-[var(--pg-border)] shadow-pronto-hero overflow-hidden backdrop-blur-[20px]">
      <BagHeader branch={resolved.branch} beans={resolved.branch === "new_bag" ? (resolved.newBag?.name || "From your photo") : beansLabel} />

      <div className="px-[18px] pt-4 pb-[18px]">
        {/* Today's plan */}
        <div className="font-sf text-[10.5px] font-bold uppercase tracking-[0.06em] text-pronto-text-3 mb-2">Today's plan</div>
        <Segmented variant="pill" radius={12} options={BRANCHES} value={resolved.branch} onChange={setForced} className="mb-2" />
        <div className="flex items-center gap-1.5 font-sf text-[11px] text-pronto-text-3 mb-[15px]">
          <span className="text-pronto-navy" aria-hidden="true">◇</span>
          <span>{forced ? "Picked" : "Auto-picked"} · {resolved.reason}</span>
        </div>

        {isGate ? (
          <BrewRating brewLog={resolved.yesterdayBrew} beanName={bean.name} onSubmit={rateYesterday} />
        ) : resolved.branch === "new_bag" ? (
          <MorningNewBag store={store} session={session} resolved={resolved} onComplete={onComplete} embedded />
        ) : resolved.branch === "tweak" ? (
          <MorningTweak store={store} session={session} resolved={resolved} onComplete={onComplete} embedded />
        ) : resolved.branch === "blend" ? (
          <MorningBlend store={store} session={session} resolved={resolved} onComplete={onComplete} embedded />
        ) : (
          <>
            {/* ── Repeat — the auto-picked recipe, verbatim ── */}
            <Segmented variant="pill" radius={11} options={MODES} value={mode} onChange={(m) => { setModeTouched(true); setMode(m); }} className="mb-2" />

            {/* Canonical batch sizes — one tap to a 4/6/8-cup pour. */}
            <Segmented
              variant="pill" radius={11}
              options={[...new Set([4, 6, 8, defaultCups])].sort((a, b) => a - b).map((c) => ({ id: c, label: `${c} cups` }))}
              value={prefCups}
              onChange={(c) => setCupsChoice(c)}
              className="mb-[13px]"
            />

            {/* Cold brew: when do you want to drink it? Drives the steep plan. */}
            {cold && (
              <>
                <div className="flex items-center justify-between rounded-[12px] px-3.5 py-2 mb-1.5" style={{ background: "rgba(63,74,99,0.05)" }}>
                  <label htmlFor="cold-ready-by" className="font-sf text-[13px] text-pronto-ink">Ready by</label>
                  <input
                    id="cold-ready-by"
                    type="time"
                    value={readyByTime}
                    onChange={(e) => setReadyByTime(e.target.value || "06:00")}
                    className="font-sf text-[14px] font-semibold text-pronto-navy bg-transparent outline-none"
                  />
                </div>
                {coldPlan && (
                  <div className="font-sf text-[11.5px] text-pronto-text-3 mb-3 leading-snug">
                    Start now → ready {coldPlan.readyBy.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} · {coldPlan.durationHours} h steep.
                    {" "}{coldPlan.warn || coldPlan.note}
                  </div>
                )}
              </>
            )}

            <div className="font-sf text-[13px] font-semibold text-pronto-navy">Today's pour · {cups} cups</div>
            {scaledCups != null && (
              <div className="font-sf text-[11px] text-pronto-text-3 mt-0.5">
                ~{weightLeft}g left in the bag — scaled down from {prefCups} cups so it brews well
              </div>
            )}
            <DoseGrind dose={doseG} grind={grindVal} cups={cups} />
            <SpecStrip tempDisp={tempDisp} ratioDisp={ratioDisp} bloomLabel={bloomLabel} bloomVal={bloomVal} />

            {/* ✎ Adjust recipe */}
            <button
              onClick={() => setAdjustOpen((v) => !v)}
              aria-expanded={adjustOpen}
              className="w-full flex items-center justify-center gap-1.5 mt-[13px] py-[9px] rounded-[12px] font-sf text-[13px] font-semibold text-pronto-navy"
              style={{ border: "1px solid rgba(63,74,99,0.25)" }}
            >
              ✎ Adjust recipe
            </button>
            {adjustOpen && (
              <div className="mt-2 rounded-[14px] px-[13px] py-1.5" style={{ background: "rgba(63,74,99,0.05)" }}>
                <StepRow
                  label="Dose"
                  value={`${doseG}g`}
                  // Floor of 8g, not 30 — a bag-scaled 2-cup dose can sit below
                  // 30, and the old floor made the "−" button RAISE the dose.
                  onDown={() => setDoseOverride((d) => Math.max(8, (d ?? doseG) - 2))}
                  onUp={() => setDoseOverride((d) => Math.min(90, (d ?? doseG) + 2))}
                  divider
                />
                <StepRow
                  label="Ode grind"
                  value={grindVal}
                  // One dial step per tap on the user's own grinder scale (Ode:
                  // 0.1 — a real correction is 0.2–0.4; whole clicks overshoot).
                  onDown={() => setGrindDelta((g) => Math.max(-10 * grindStep, +(g - grindStep).toFixed(grindDecimals)))}
                  onUp={() => setGrindDelta((g) => Math.min(10 * grindStep, +(g + grindStep).toFixed(grindDecimals)))}
                  divider
                />
                <StepRow
                  label="Temp"
                  value={tempDisp}
                  onDown={() => setTempDelta((t) => Math.max(-8, t - 1))}
                  onUp={() => setTempDelta((t) => Math.min(8, t + 1))}
                />
                <div className="font-sf text-[11px] text-pronto-text-3 pt-1.5 pb-0.5 leading-[1.4]">
                  Dose &amp; grind apply to this brew only. Changing <b>temp</b> updates the Aiden profile.
                </div>
              </div>
            )}

            {/* 📝 Notes — expected flavor + tomorrow's pre-planned move */}
            <RecipeNotes beans={[bean]} brew={adjustedBrew} grinder={store.gear?.grinder} className="mt-[14px]" />

            {/* Aiden profile banner */}
            <AidenProfileBanner
              store={store}
              bean={bean}
              brew={adjustedBrew}
              branch="repeat"
              tempChanged={tempDelta !== 0}
              className="mt-2.5"
            />

            {/* Started the Aiden — the ritual's last physical step IS the log */}
            <button
              onClick={startBrew}
              disabled={busy}
              className="w-full h-[46px] rounded-[14px] font-sf text-[15px] font-semibold text-pronto-navy mt-3 disabled:opacity-60"
              style={{ background: "rgba(63,74,99,0.1)" }}
            >
              {busy ? "Logging…" : "Started the Aiden ✓"}
            </button>

            <div className="text-center font-sf text-[12px] text-pronto-text-2 mt-[9px]">{FOOT.repeat}</div>
          </>
        )}
      </div>
    </div>
  );
}

// Shared readout primitives — reused by the embedded branch bodies for a uniform
// dose/grind/spec presentation matching the prototype.
export { DoseGrind, SpecStrip, FOOT };
